import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { getQueryParams } from "@/utils/httpRequest";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics, sendCollectionToAnalytics } from "@/utils/analytics";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import { showNotification } from "@/redux/actions/notification";
import { requestCloseModal } from "@/redux/actions/modal";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";
import {
  LENDER_BOARD_OFFER_REDUCER_PATH,
  LENDER_BOARD_OFFER_RELATED_TAGS,
  LENDER_BOARD_OFFER_SERVICE_TAG,
  LENDER_BOARD_OFFER_FILE_SERVICE_TAG,
} from "@/features/LenderBoard/Offer/utils/constants/lenderBoardOfferServiceTags";
import { lenderBoardService } from "@/features/LenderBoard/Management/store/lenderBoardService";

export const lenderOfferService = createApi({
  reducerPath: LENDER_BOARD_OFFER_REDUCER_PATH,
  tagTypes: LENDER_BOARD_OFFER_RELATED_TAGS,
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    // ──────────────── Query Endpoints ────────────────

    getLenderBoardOffers: builder.query({
      query: ({ loanId, lenderBoardId, ...params }) =>
        `loans/${loanId}/lender-boards/${lenderBoardId}/offers?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ lenderBoardOfferId: id }) => ({ type: LENDER_BOARD_OFFER_SERVICE_TAG, id })),
              { type: LENDER_BOARD_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_BOARD_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),

    getLenderBoardOffer: builder.query({
      query: ({ loanId, lenderBoardId, lenderBoardOfferId }) =>
        `loans/${loanId}/lender-boards/${lenderBoardId}/offers/${lenderBoardOfferId}`,
      providesTags: (result, error, { lenderBoardOfferId: id }) => [{ type: LENDER_BOARD_OFFER_SERVICE_TAG, id }],
    }),

    getLenderBoardOfferFiles: builder.query({
      query: ({ loanId, lenderBoardId, lenderBoardOfferId, ...params }) =>
        `loans/${loanId}/lender-boards/${lenderBoardId}/offers/${lenderBoardOfferId}/files?${getQueryParams(params)}`,
      providesTags: result =>
        result
          ? [
              ...result.items.map(({ lenderBoardOfferFileId: id }) => ({
                type: LENDER_BOARD_OFFER_FILE_SERVICE_TAG,
                id,
              })),
              { type: LENDER_BOARD_OFFER_FILE_SERVICE_TAG, id: LIST_SERVICE_TAG },
            ]
          : [{ type: LENDER_BOARD_OFFER_FILE_SERVICE_TAG, id: LIST_SERVICE_TAG }],
    }),

    // ──────────────── Offer Mutation Endpoints ────────────────

    createLenderBoardOffer: builder.mutation({
      query: body => ({
        url: `loans/${body.loanId}/lender-boards/${body.lenderBoardId}/offers`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: LENDER_BOARD_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_OFFER_CREATED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Offer has been created successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderBoardService.util.invalidateTags([{ type: "LenderBoard", id: data?.lenderBoardId }]));
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    updateLenderBoardOffer: builder.mutation({
      query: ({ loanId, lenderBoardId, lenderBoardOfferId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/offers/${lenderBoardOfferId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: LENDER_BOARD_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_BOARD_OFFER_SERVICE_TAG, id: arg.lenderBoardOfferId },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_OFFER_UPDATED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Offer has been updated successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    deleteLenderBoardOffer: builder.mutation({
      query: ({ loanId, lenderBoardId, lenderBoardOfferId }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/offers/${lenderBoardOfferId}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: LENDER_BOARD_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_OFFER_DELETED);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Offer has been deleted successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderBoardService.util.invalidateTags([{ type: "LenderBoard", id: data?.lenderBoardId }]));
          dispatch(setProcessingState(false));
          dispatch(requestCloseModal());
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    // ──────────────── Offer File Mutation Endpoints ────────────────

    createLenderBoardOfferFiles: builder.mutation({
      query: ({ loanId, lenderBoardId, lenderBoardOfferId, uploadIds }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/offers/${lenderBoardOfferId}/files`,
        method: "POST",
        body: { uploadIds },
      }),
      invalidatesTags: () => [
        { type: LENDER_BOARD_OFFER_FILE_SERVICE_TAG, id: LIST_SERVICE_TAG },
        { type: LENDER_BOARD_OFFER_SERVICE_TAG, id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_OFFER_FILE_UPLOADED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description:
                  data.length === 1 ? "File has been uploaded successfully." : "Files have been uploaded successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    deleteLenderBoardOfferFile: builder.mutation({
      query: ({ loanId, lenderBoardId, lenderBoardOfferId, lenderBoardOfferFileId }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/offers/${lenderBoardOfferId}/files/${lenderBoardOfferFileId}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: LENDER_BOARD_OFFER_FILE_SERVICE_TAG, id: LIST_SERVICE_TAG }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          const data = await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_OFFER_FILE_DELETED, data);

          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "File has been deleted successfully.",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(setProcessingState(false));
        } catch (err) {
          Bugsnag.notify(err);
          dispatch(setProcessingState(false));
        }
      },
    }),

    previewLenderBoardOfferFile: builder.mutation({
      query({ loanId, lenderBoardId, lenderBoardOfferId, lenderBoardOfferFileId }) {
        return {
          url: `loans/${loanId}/lender-boards/${lenderBoardId}/offers/${lenderBoardOfferId}/files/${lenderBoardOfferFileId}/preview`,
          method: "GET",
        };
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.fileUrl;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.download = data.file.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_OFFER_FILE_PREVIEWED, data.file);
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),

    downloadLenderBoardOfferFile: builder.mutation({
      query({ loanId, lenderBoardId, lenderBoardOfferId, lenderBoardOfferFileId }) {
        return {
          url: `loans/${loanId}/lender-boards/${lenderBoardId}/offers/${lenderBoardOfferId}/files/${lenderBoardOfferFileId}/download`,
          method: "GET",
        };
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = data.fileUrl;
          a.download = data.file.fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_OFFER_FILE_DOWNLOADED, data.file);
        } catch (err) {
          Bugsnag.notify(err);
        }
      },
    }),
  }),
});

export const {
  // Query hooks
  useGetLenderBoardOffersQuery,
  useGetLenderBoardOfferQuery,
  useGetLenderBoardOfferFilesQuery,
  // Offer mutation hooks
  useCreateLenderBoardOfferMutation,
  useUpdateLenderBoardOfferMutation,
  useDeleteLenderBoardOfferMutation,
  // Offer file mutation hooks
  useCreateLenderBoardOfferFilesMutation,
  useDeleteLenderBoardOfferFileMutation,
  usePreviewLenderBoardOfferFileMutation,
  useDownloadLenderBoardOfferFileMutation,
} = lenderOfferService;
